import { useEffect, useRef, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAccessToken, getGuId, getLoginId, getRefreshToken, getSidebarCollapsed, setSidebarCollapsed } from "../../utils/SecurityService.js";
import minilogo from "../../assets/images/logo-small.svg";
import logo from "../../assets/images/logo-big.svg"; // New: Import the minimized logo
import arrowwithgreenbg from "../../assets/images/arrow-with-green-bg.svg";
import dashboard from "../../assets/images/icons/dashboard.svg";
import bookings from "../../assets/images/icons/bookings.svg";
import colleagues from "../../assets/images/icons/colleagues.svg";
import suitcase from "../../assets/images/icons/suitcase.svg";
import settings from "../../assets/images/icons/settings.svg";
import map from "../../assets/images/icons/minimap.svg";
import EditSquare from "../../assets/images/icons/edit-square.svg";
import logout from "../../assets/images/icons/logout.svg";
import { UserLogout_api } from "../Login/LoginAPI.js";

const MAP_TO_CALENDAR_SYNC_KEY = "mapToCalendarUseSharedZone";
const SIDEBAR_BRANDING_CACHE_KEY = "sidebarBrandingCache";

export default function Sidebar({ IsThumbnailPresent, CompanyId }) {
    const sidebarRef = useRef(null);
    const loadedLogoUrlsRef = useRef(new Set());
    const { t } = useTranslation("common");
    const location = useLocation();
    const [isSidebarActive, setIsSidebarActive] = useState(() => !getSidebarCollapsed());
    const [displayedLogoSrc, setDisplayedLogoSrc] = useState(null);
    const getCachedBranding = () => {
        try {
            const raw = sessionStorage.getItem(SIDEBAR_BRANDING_CACHE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    };

    const cachedBranding = getCachedBranding();
    const resolvedCompanyId = Number(CompanyId) > 0
        ? Number(CompanyId)
        : Number(cachedBranding?.CompanyId) > 0
            ? Number(cachedBranding.CompanyId)
            : 0;
    const resolvedThumbnailPresent =
        Number(CompanyId) > 0
            ? IsThumbnailPresent
            : cachedBranding?.IsThumbnailPresent;
    const hasCompanyBranding = resolvedCompanyId > 0;
    const expandedLogoUrl = hasCompanyBranding
        ? `https://ronlivestaging.s3.amazonaws.com/companylogo/${resolvedCompanyId}/logo.png`
        : null;
    const collapsedLogoUrl = hasCompanyBranding
        ? (resolvedThumbnailPresent === 1
            ? `https://ronlivestaging.s3.amazonaws.com/companythumbnail/${resolvedCompanyId}/logo.png`
            : expandedLogoUrl)
        : null;
    const targetLogo = hasCompanyBranding
        ? (isSidebarActive ? expandedLogoUrl : collapsedLogoUrl)
        : resolvedCompanyId === 0
            ? null
            : (isSidebarActive ? logo : minilogo);
    const defaultLogo = hasCompanyBranding
        ? (isSidebarActive ? logo : minilogo)
        : resolvedCompanyId === 0
            ? null
            : (isSidebarActive ? logo : minilogo);
    const menuItems = [
        //{ to: "/booking",textKey: "test" },
        { to: "/booking", textKey: "react_home", icon: bookings, tooltip: t("react_home") },
        // { to: "/calendar", textKey: "Calendar", icon: dashboard, tooltip: "Calendar" },
        { to: "/calendar", match: ["/calendar", "/weekCalendar"], textKey: "calendar", icon: dashboard, tooltip: t("calendar") },
        { to: "/map", textKey: "map", icon: map, tooltip: t("map") },
        { to: "/newbooking", textKey: "react_new_booking", icon: EditSquare, tooltip: t("react_new_booking") },
        { to: "/colleagues", textKey: "react_find_colleagues", icon: colleagues, tooltip: t("react_find_colleagues") },
        { to: "/roles", textKey: "react_employee_roles", icon: suitcase, tooltip: t("react_employee_roles") },
        { to: "/profile", textKey: "react_profile", icon: settings, tooltip: t("react_profile") },
    ];

    const handleMenuClick = () => {
        const container = document.querySelector(".my-container");

        setIsSidebarActive((prevState) => {
            const nextState = !prevState;
            const isCollapsed = !nextState;

            setSidebarCollapsed(isCollapsed);

            if (container) {
                container.classList.toggle("active-cont", nextState);
            }

            return nextState;
        });
    };

    useEffect(() => {
        const container = document.querySelector(".my-container");

        if (container) {
            container.classList.toggle("active-cont", isSidebarActive);
        }
    }, [isSidebarActive]);

    useEffect(() => {
        if (Number(CompanyId) > 0) {
            sessionStorage.setItem(
                SIDEBAR_BRANDING_CACHE_KEY,
                JSON.stringify({
                    CompanyId: Number(CompanyId),
                    IsThumbnailPresent,
                })
            );
        }
    }, [CompanyId, IsThumbnailPresent]);

    useEffect(() => {
        let isCancelled = false;

        if (!targetLogo) {
            setDisplayedLogoSrc(null);
            return undefined;
        }

        if (loadedLogoUrlsRef.current.has(targetLogo)) {
            setDisplayedLogoSrc(targetLogo);
            return undefined;
        }

        if (targetLogo === defaultLogo && defaultLogo) {
            setDisplayedLogoSrc(defaultLogo);
            return undefined;
        }

        setDisplayedLogoSrc(null);

        const image = new Image();
        image.src = targetLogo;

        image.onload = () => {
            if (!isCancelled) {
                loadedLogoUrlsRef.current.add(targetLogo);
                setDisplayedLogoSrc(targetLogo);
            }
        };

        image.onerror = () => {
            if (!isCancelled) {
                setDisplayedLogoSrc(defaultLogo || null);
            }
        };

        return () => {
            isCancelled = true;
        };
    }, [targetLogo, defaultLogo]);

    //  const handleLogout = (e) => {
    //     e.preventDefault();
    //     localStorage.clear();
    //     sessionStorage.clear();

    //     // VERY important: replace history
    //     navigate("/Login", { replace: true });
    // };
    const history = useHistory();

    
    const handleLogout = async (e) => {
        e.preventDefault();

        try {
             const requestParams = {
            GuId: getGuId(),
            RefreshToken: getRefreshToken(),
        };
            await UserLogout_api(requestParams);
        } catch (error) {
            console.error("Logout API failed:", error);
        }

        localStorage.clear();
        sessionStorage.clear();

        // VERY important: replace history
        history.push("/", { replace: true });
    };

    return (
        <div
            className={`side-navbar d-flex flex-wrap flex-column justify-content-between ${isSidebarActive ? "active-nav" : ""}`}
            id="sidebar"
            ref={sidebarRef}
        >
            {/* <a href="#" className="nav-link text-center pt-4 logo">
                {isSidebarActive ? (
                    <img src={logo} alt="Full Logo" className="img-fluid logo" />
                ) : (
                    <img src={minilogo} alt="Mini Logo" className="img-fluid logo" />
                )}
            </a> */}

            <div className="nav-link text-center pt-4 logo">
                <div className={`logo-container ${isSidebarActive ? "expanded" : "collapsed"}`}>
                    {displayedLogoSrc ? (
                        <img
                            src={displayedLogoSrc}
                            alt="Logo"
                            className="img-fluid"
                        />
                    ) : null}
                </div>
            </div>

            <div className="open-close">
                <button type="button" className="btn border-0" id="menu-btn" onClick={handleMenuClick}>
                    <img src={arrowwithgreenbg} alt="" />
                </button>
            </div>
            <div id="accordian">
                <ul className="show-dropdown menu-style">
                    {menuItems.map((item) => (
                        // <li
                        //     key={item.to}
                        //     className={location.pathname === item.to ? "active" : ""}
                        // >
                        <li
                            key={item.to}
                            className={
                                item.match
                                    ? item.match.includes(location.pathname)
                                        ? "active"
                                        : ""
                                    : location.pathname === item.to
                                        ? "active"
                                        : ""
                            }
                        >
                            <NavLink
                                to={item.to}
                                data-tooltip={item.tooltip}
                                onClick={(e) => {
                                    if (location.pathname === "/map" && item.to === "/calendar") {
                                        sessionStorage.setItem(MAP_TO_CALENDAR_SYNC_KEY, "1");
                                    }

                                    if (location.pathname === item.to) {
                                        // 🔥 Force refresh
                                        e.preventDefault();
                                        window.location.reload();
                                    }
                                }}
                            >
                                 <img src={item.icon} alt="" />
                                <span>{t(item.textKey)}</span>
                            </NavLink>

                            {/* <NavLink
                                to={item.to}
                                data-tooltip={item.tooltip}
                            >
                                <img src={item.icon} alt="" />
                                <span>{t(item.textKey)}</span>
                            </NavLink> */}
                        </li>
                    ))}
                </ul>
            </div>
            <div className="button-style">
                <button type="button" onClick={handleLogout} className="green-outline-with-icon mx-auto mb-4 logout-btn">
                    <img src={logout} alt="" /> {t("logout")}
                </button>
            </div>
            {/* <div className="submit-btn">
            <button type="submit" id="logoutBtn" onClick={handleLogout}>
                Logout
            </button>
        </div> */}
        </div>
    );
}
